import { useState } from 'react';
import { useOpenTasks, useCompletedTasks } from '@/hooks/use-task-queries';
import { TaskTable } from '@/components/task-table';
import { CheckCircle2, Archive, ClipboardList, AlertCircle } from 'lucide-react';
import { useTableRefresh } from '@/hooks/use-table-refresh';
import { queryKeys } from '@/lib/query-keys';

const PAGE_SIZE = 10;

export function AgentTasks() {
  const refreshTasks = useTableRefresh(queryKeys.tasks.all());
  const [openPage, setOpenPage] = useState(1);
  const [openSortBy, setOpenSortBy] = useState<string | undefined>('beforeDate');
  const [openSortOrder, setOpenSortOrder] = useState<'asc' | 'desc'>('asc');
  const [openSearch, setOpenSearch] = useState<string | undefined>();

  const [completedPage, setCompletedPage] = useState(1);
  const [completedSortBy, setCompletedSortBy] = useState<string | undefined>('completedAt');
  const [completedSortOrder, setCompletedSortOrder] = useState<'asc' | 'desc'>('desc');
  const [completedSearch, setCompletedSearch] = useState<string | undefined>();

  const {
    data: openTasks,
    total: openTotal,
    isLoading: openLoading,
    isFetching: openFetching,
    isError: openError,
  } = useOpenTasks({
    viewPage: openPage,
    viewPageSize: PAGE_SIZE,
    sortBy: openSortBy,
    sortOrder: openSortOrder,
    search: openSearch,
  });

  const {
    data: completedTasks,
    total: completedTotal,
    isLoading: completedLoading,
    isFetching: completedFetching,
    isError: completedError,
  } = useCompletedTasks({
    viewPage: completedPage,
    viewPageSize: PAGE_SIZE,
    sortBy: completedSortBy,
    sortOrder: completedSortOrder,
    search: completedSearch,
  });

  return (
    <div className="space-y-8">
      <div id="open-tasks">
        <div className="mb-4 flex items-center gap-2">
          <ClipboardList className="h-6 w-6 text-primary" />
          <h2 className="font-display text-2xl font-medium tracking-tight text-foreground">
            Open Report Tasks
          </h2>
        </div>
        <TaskTable
          tasks={openTasks}
          type="open"
          isLoading={openLoading}
          isFetching={openFetching}
          onRefresh={refreshTasks}
          pagination={{
            page: openPage,
            pageSize: PAGE_SIZE,
            total: openTotal,
            onPageChange: setOpenPage,
          }}
          sorting={{
            sortBy: openSortBy,
            sortOrder: openSortOrder,
            onSortChange: (by, order) => {
              setOpenSortBy(by);
              setOpenSortOrder(order ?? 'asc');
            },
          }}
          onSearchChange={(q) => {
            setOpenSearch(q || undefined);
            setOpenPage(1);
          }}
          emptyState={
            openError
              ? {
                  icon: <AlertCircle className="h-6 w-6 text-muted-foreground" />,
                  title: 'Failed to load tasks',
                  description: 'There was a problem fetching open tasks. Please try again.',
                }
              : {
                  icon: <CheckCircle2 className="h-6 w-6 text-muted-foreground" />,
                  title: 'No open tasks at the moment',
                }
          }
        />
      </div>

      <div id="completed-tasks">
        <div className="mb-4 flex items-center gap-2">
          <Archive className="h-6 w-6 text-muted-foreground" />
          <h2 className="font-display text-2xl font-medium tracking-tight text-foreground">
            Completed Tasks
          </h2>
        </div>
        <TaskTable
          tasks={completedTasks}
          type="completed"
          isLoading={completedLoading}
          isFetching={completedFetching}
          onRefresh={refreshTasks}
          pagination={{
            page: completedPage,
            pageSize: PAGE_SIZE,
            total: completedTotal,
            onPageChange: setCompletedPage,
          }}
          sorting={{
            sortBy: completedSortBy,
            sortOrder: completedSortOrder,
            onSortChange: (by, order) => {
              setCompletedSortBy(by);
              setCompletedSortOrder(order ?? 'desc');
            },
          }}
          onSearchChange={(q) => {
            setCompletedSearch(q || undefined);
            setCompletedPage(1);
          }}
          emptyState={
            completedError
              ? {
                  icon: <AlertCircle className="h-6 w-6 text-muted-foreground" />,
                  title: 'Failed to load tasks',
                  description: 'There was a problem fetching completed tasks. Please try again.',
                }
              : {
                  icon: <Archive className="h-6 w-6 text-muted-foreground" />,
                  title: 'No completed tasks yet',
                }
          }
        />
      </div>
    </div>
  );
}
